import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { ContextStatus, SubscriptionStatus, EDegradedSubscriptionStatus } from '@leedpulse-monorepo/enums';
import AuthenticationContext, { DegradedSubscription } from '../auth/AuthenticationContext';
import CompanyContext from '../company/CompanyContext';
import { getCompanySubscriptions } from '../../../domain/use_cases/subscription/getCompanySubscriptions';
import { ISubscription } from '@leedpulse-monorepo/interfaces';

interface SubscriptionContextValue {
  subscriptions: ISubscription[];
  status: ContextStatus;
  refreshSubscriptions: () => Promise<void>;

}

const SubscriptionContext = createContext<SubscriptionContextValue>({
  subscriptions: [],
  status: ContextStatus.IDLE,
  refreshSubscriptions: async () => undefined,
});

export const SubscriptionProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { activeCompany } = useContext(CompanyContext);
  const { authentication, setDegradedSubscriptions } = useContext(AuthenticationContext);
  const [subscriptions, setSubscriptions] = useState<ISubscription[]>([]);
  const [status, setStatus] = useState<ContextStatus>(ContextStatus.IDLE);

  const fetchSubscriptions = useCallback(async () => {
    if (!authentication || !activeCompany?.id) {
      setSubscriptions([]);
      setDegradedSubscriptions([]);
      setStatus(ContextStatus.SUCCESS);
      return;
    }

    setStatus(ContextStatus.LOADING);

    try {
      const fetched = await getCompanySubscriptions(
        { company_id: activeCompany.id },
        authentication,
      );

      setSubscriptions(fetched);

      const degraded: DegradedSubscription[] = fetched
        .filter((s) => s.status === SubscriptionStatus.CANCELLED || s.status === SubscriptionStatus.PAUSED)
        .map((s) => ({
          id: s.id,
          status: s.status as unknown as EDegradedSubscriptionStatus,
        }));
      setDegradedSubscriptions(degraded);
      setStatus(ContextStatus.SUCCESS);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      setStatus(ContextStatus.ERROR);
    }
  }, [authentication, activeCompany, setDegradedSubscriptions]);

  const refreshSubscriptions = useCallback(async () => {
    await fetchSubscriptions();
  }, [fetchSubscriptions]);

  useEffect(() => {
    // Synchro avec un contexte qui fetche : l'etat local suit le resultat d'un
    // chargement declenche ici. Architecture Context + use case du repo.
    // eslint-disable-next-line react/set-state-in-effect
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  return (
    <SubscriptionContext.Provider
      value={{
        subscriptions,
        status,
        refreshSubscriptions,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
